import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { FolderOpen } from "lucide-react";
import { z } from "zod";
import {
  currentBusinessWeek,
  fromYmd,
  isValidYmd,
  startOfWeek,
  weekSegments,
  ymd,
  type AssignmentDTO,
} from "@contractorhub/shared";
import { MultiProjectTimesheetEditor } from "../../../components/multi-project-timesheet-editor";
import { recentTimesheetsQuery } from "../../../components/timesheet/week-context-rail";
import { Card, EmptyState } from "../../../components/ui";
import { myAssignmentsQuery, myTimesheetQuery } from "../../../lib/queries";

const searchSchema = z.object({
  project: z.string().optional(),
  week: z.string().refine(isValidYmd).optional(),
});

/**
 * One query per assignment per period of the week (TIME-1): a week straddling a
 * month end is two timesheets for every project, filed and approved apart. The
 * week is resolved here rather than left to the server so the halves can be
 * named — "this week" is the business's week either way.
 */
function mondayOf(week: string | undefined): Date {
  return week ? startOfWeek(fromYmd(week)) : currentBusinessWeek();
}

function sheetQueries(assignments: AssignmentDTO[], week: string | undefined) {
  const monday = mondayOf(week);
  return assignments.flatMap((assignment) =>
    weekSegments(monday).map((segment) =>
      myTimesheetQuery({
        projectId: assignment.projectId,
        week: ymd(monday),
        period: ymd(segment.periodStart),
      })
    )
  );
}

export const Route = createFileRoute("/_authed/_contractor/my-timesheet")({
  validateSearch: searchSchema,
  // Re-run the loader when the week/project changes, not just on mount.
  loaderDeps: ({ search }) => ({ project: search.project, week: search.week }),
  loader: async ({ context, deps }) => {
    const assignmentData =
      await context.queryClient.ensureQueryData(myAssignmentsQuery);
    // The recent weeks ride alongside the sheets, not after them: the editor
    // opens a fresh week on the project worked most recently (TIME-14).
    await Promise.all([
      ...sheetQueries(assignmentData.assignments, deps.week).map((query) =>
        context.queryClient.ensureQueryData(query)
      ),
      context.queryClient.ensureQueryData(recentTimesheetsQuery(mondayOf(deps.week))),
    ]);
  },
  component: MyTimesheetPage,
});

function MyTimesheetPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const { data: assignmentsData } = useSuspenseQuery(myAssignmentsQuery);
  const sheets = useSuspenseQueries({
    queries: sheetQueries(assignmentsData.assignments, search.week),
  });
  const { data: recent } = useSuspenseQuery(recentTimesheetsQuery(mondayOf(search.week)));

  if (assignmentsData.assignments.length === 0) {
    return (
      <Card className="mx-auto mt-16 max-w-lg">
        <EmptyState
          icon={FolderOpen}
          title="No projects assigned"
          description="You aren't assigned to any projects yet. Please contact your Simplok admin to get set up."
        />
      </Card>
    );
  }

  return (
    <MultiProjectTimesheetEditor
      key={sheets[0]!.data.weekStart}
      initialSheets={sheets.map((query) => query.data)}
      recentRows={recent.timesheets}
      assignments={assignmentsData.assignments}
      focusProject={search.project}
      // `project` rides along through week navigation — stepping back a week
      // from a deep link should keep you on the project you arrived for.
      onNavigate={(week) =>
        void navigate({
          to: "/my-timesheet",
          search: { week, project: search.project },
        })
      }
    />
  );
}
